import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
    Plus, Trash2, Edit, Save, X, Calendar, Users, Clock, 
    DoorOpen, Package, UserCheck, Euro, Building2, Check,
    Download, Mail, ChevronLeft, ChevronRight, Eye, FileText, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Room Management Component
const RoomManager = () => {
    const [rooms, setRooms] = useState([]);
    const [editingRoom, setEditingRoom] = useState(null);
    const [isNew, setIsNew] = useState(false);

    useEffect(() => {
        fetchRooms();
    }, []);

    const fetchRooms = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/booking/rooms?include_inactive=true`);
            setRooms(response.data);
        } catch (error) {
            toast.error("Fehler beim Laden der Räume");
        }
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('token');
            if (isNew) {
                await axios.post(`${API_URL}/api/booking/rooms`, editingRoom, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Raum erstellt");
            } else {
                await axios.put(`${API_URL}/api/booking/rooms/${editingRoom._id}`, editingRoom, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Raum aktualisiert");
            }
            setEditingRoom(null);
            setIsNew(false);
            fetchRooms();
        } catch (error) {
            toast.error("Fehler beim Speichern");
        }
    };

    const handleDelete = async (roomId) => {
        if (!window.confirm("Raum wirklich löschen?")) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/booking/rooms/${roomId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Raum gelöscht");
            fetchRooms();
        } catch (error) {
            toast.error("Fehler beim Löschen");
        }
    };

    const newRoom = () => {
        setIsNew(true);
        setEditingRoom({
            name: "",
            description: "",
            capacity: 10,
            price_per_hour: 0,
            amenities: [],
            is_active: true
        });
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Buchungsräume</h3>
                <Button onClick={newRoom} size="sm">
                    <Plus size={16} className="mr-1" /> Neuer Raum
                </Button>
            </div>

            {editingRoom && (
                <Card className="border-2 border-blue-500">
                    <CardContent className="p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium">Name</label>
                                <Input 
                                    value={editingRoom.name}
                                    onChange={(e) => setEditingRoom({...editingRoom, name: e.target.value})}
                                    placeholder="z.B. Seeblick"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Kapazität</label>
                                <Input 
                                    type="number"
                                    value={editingRoom.capacity}
                                    onChange={(e) => setEditingRoom({...editingRoom, capacity: parseInt(e.target.value)})}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium">Beschreibung</label>
                            <textarea 
                                className="w-full border rounded-md p-2 text-sm"
                                rows={2}
                                value={editingRoom.description || ""}
                                onChange={(e) => setEditingRoom({...editingRoom, description: e.target.value})}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium">Preis pro Stunde (€)</label>
                                <Input 
                                    type="number"
                                    step="0.01"
                                    value={editingRoom.price_per_hour}
                                    onChange={(e) => setEditingRoom({...editingRoom, price_per_hour: parseFloat(e.target.value)})}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Ausstattung (kommagetrennt)</label>
                                <Input 
                                    value={(editingRoom.amenities || []).join(", ")}
                                    onChange={(e) => setEditingRoom({...editingRoom, amenities: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})}
                                    placeholder="Beamer, WLAN, Whiteboard"
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <input 
                                type="checkbox"
                                checked={editingRoom.is_active !== false}
                                onChange={(e) => setEditingRoom({...editingRoom, is_active: e.target.checked})}
                            />
                            <label className="text-sm">Aktiv (buchbar)</label>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={handleSave} size="sm">
                                <Save size={14} className="mr-1" /> Speichern
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => { setEditingRoom(null); setIsNew(false); }}>
                                <X size={14} className="mr-1" /> Abbrechen
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="grid gap-3">
                {rooms.map(room => (
                    <Card key={room._id} className={`${!room.is_active ? 'opacity-50' : ''}`}>
                        <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h4 className="font-semibold flex items-center gap-2">
                                        <DoorOpen size={18} />
                                        {room.name}
                                        {!room.is_active && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">Inaktiv</span>}
                                    </h4>
                                    <p className="text-sm text-gray-500 mt-1">{room.description}</p>
                                    <div className="flex gap-4 mt-2 text-sm text-gray-600">
                                        <span className="flex items-center gap-1"><Users size={14} /> {room.capacity} Pers.</span>
                                        <span className="flex items-center gap-1"><Euro size={14} /> {room.price_per_hour}€/h</span>
                                    </div>
                                    {room.amenities?.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {room.amenities.map((a, i) => (
                                                <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded">{a}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="sm" onClick={() => { setEditingRoom(room); setIsNew(false); }}>
                                        <Edit size={14} />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(room._id)}>
                                        <Trash2 size={14} className="text-red-500" />
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
};

// Booking Overview Component - EXTENDED with Calendar, Export, Email Preview
const BookingOverview = () => {
    const [bookings, setBookings] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [filter, setFilter] = useState({ room: '', status: '' });
    const [viewMode, setViewMode] = useState('list'); // list, calendar
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [emailPreview, setEmailPreview] = useState(null);
    const [selectedBooking, setSelectedBooking] = useState(null);

    useEffect(() => {
        fetchBookings();
        fetchRooms();
    }, []);

    const fetchBookings = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/booking/bookings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBookings(response.data);
        } catch (error) {
            toast.error("Fehler beim Laden der Buchungen");
        }
    };

    const fetchRooms = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/booking/rooms`);
            setRooms(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    const handleCancel = async (bookingId) => {
        if (!window.confirm("Buchung wirklich stornieren?")) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/booking/bookings/${bookingId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Buchung storniert");
            fetchBookings();
        } catch (error) {
            toast.error("Fehler beim Stornieren");
        }
    };

    // Export to CSV
    const exportToCSV = () => {
        const headers = ['Datum', 'Uhrzeit', 'Raum', 'Name', 'E-Mail', 'Teilnehmer', 'Netto', 'MwSt', 'Brutto', 'Status', 'Zweck', 'Besondere Wünsche'];
        const rows = filteredBookings.map(b => {
            const netPrice = b.total_price || 0;
            const vatAmount = netPrice * 0.19;
            const grossPrice = netPrice * 1.19;
            return [
                formatDate(b.start_time),
                `${formatTime(b.start_time)}-${formatTime(b.end_time)}`,
                b.room_name || '',
                b.user_name || b.guest_name || '',
                b.user_email || b.guest_email || '',
                b.participants_count || 1,
                netPrice.toFixed(2),
                vatAmount.toFixed(2),
                grossPrice.toFixed(2),
                b.status === 'confirmed' ? 'Bestätigt' : 'Storniert',
                b.purpose || '',
                b.special_requests || ''
            ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(';');
        });
        
        const csv = [headers.join(';'), ...rows].join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `buchungen_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Export erfolgreich');
    };

    // Send test email
    const sendTestEmail = async (bookingId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_URL}/api/booking/bookings/${bookingId}/send-email`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Test-E-Mail gesendet');
        } catch (error) {
            toast.error('Fehler beim Senden der E-Mail');
        }
    };

    const filteredBookings = bookings.filter(b => {
        if (filter.room && b.room_id !== filter.room) return false;
        if (filter.status && b.status !== filter.status) return false;
        return true;
    }).sort((a, b) => new Date(b.start_time) - new Date(a.start_time));

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatTime = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    };

    // Calendar View Helpers
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];
        
        // Add empty slots for days before first of month
        for (let i = 0; i < (firstDay.getDay() || 7) - 1; i++) {
            days.push(null);
        }
        
        // Add all days of month
        for (let d = 1; d <= lastDay.getDate(); d++) {
            days.push(new Date(year, month, d));
        }
        
        return days;
    };

    const getBookingsForDate = (date) => {
        if (!date) return [];
        const dateStr = date.toISOString().split('T')[0];
        return filteredBookings.filter(b => {
            const bookingDate = new Date(b.start_time).toISOString().split('T')[0];
            return bookingDate === dateStr;
        });
    };

    const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    const DAYS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

    // Email Preview Component
    const generateEmailPreview = (booking) => {
        const netPrice = booking.total_price || 0;
        const vatAmount = netPrice * 0.19;
        const grossPrice = netPrice * 1.19;
        
        return `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <div style="background: #000; color: #fff; padding: 30px; text-align: center;">
                    <h1 style="margin: 0;">teamWERK</h1>
                    <p style="margin: 5px 0 0 0; font-size: 12px;">MAKE THE DREAM WORK</p>
                </div>
                <div style="padding: 30px; background: #fff;">
                    <h2 style="color: #000;">Buchungsbestätigung</h2>
                    <p>Guten Tag ${booking.user_name || booking.guest_name || 'Gast'},</p>
                    <p>Ihre Raumbuchung wurde bestätigt:</p>
                    
                    <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
                        <tr><td style="padding: 8px; border-bottom: 1px solid #eee;"><strong>Raum:</strong></td><td>${booking.room_name}</td></tr>
                        <tr><td style="padding: 8px; border-bottom: 1px solid #eee;"><strong>Datum:</strong></td><td>${formatDate(booking.start_time)}</td></tr>
                        <tr><td style="padding: 8px; border-bottom: 1px solid #eee;"><strong>Uhrzeit:</strong></td><td>${formatTime(booking.start_time)} - ${formatTime(booking.end_time)} Uhr</td></tr>
                        <tr><td style="padding: 8px; border-bottom: 1px solid #eee;"><strong>Teilnehmer:</strong></td><td>${booking.participants_count}</td></tr>
                        ${booking.purpose ? `<tr><td style="padding: 8px; border-bottom: 1px solid #eee;"><strong>Zweck:</strong></td><td>${booking.purpose}</td></tr>` : ''}
                        ${booking.special_requests ? `<tr><td style="padding: 8px; border-bottom: 1px solid #eee;"><strong>Besondere Wünsche:</strong></td><td>${booking.special_requests}</td></tr>` : ''}
                    </table>
                    
                    <div style="background: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0;">
                        <h3 style="margin: 0 0 10px 0;">Preisübersicht</h3>
                        <div style="display: flex; justify-content: space-between;"><span>Netto:</span><span>${netPrice.toFixed(2)} €</span></div>
                        <div style="display: flex; justify-content: space-between;"><span>zzgl. 19% MwSt.:</span><span>${vatAmount.toFixed(2)} €</span></div>
                        <div style="display: flex; justify-content: space-between; font-weight: bold; border-top: 1px solid #ccc; margin-top: 10px; padding-top: 10px;">
                            <span>Gesamt (brutto):</span><span>${grossPrice.toFixed(2)} €</span>
                        </div>
                    </div>
                    
                    <div style="background: #e8f4fd; padding: 15px; border-radius: 8px; margin: 20px 0;">
                        <strong>📍 Adresse:</strong><br>
                        teamWERK Erolzheim<br>
                        Espachstraße 13<br>
                        88453 Erolzheim
                    </div>
                </div>
                <div style="background: #000; color: #fff; padding: 20px; text-align: center; font-size: 12px;">
                    teamWERK Erolzheim · Espachstraße 13 · 88453 Erolzheim<br>
                    info@teamwerk-erolzheim.de
                </div>
            </div>
        `;
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-2">
                <h3 className="text-lg font-semibold">Buchungsübersicht</h3>
                <div className="flex gap-2 flex-wrap">
                    {/* View Mode Toggle */}
                    <div className="flex border rounded overflow-hidden">
                        <button 
                            className={`px-3 py-1 text-sm ${viewMode === 'list' ? 'bg-black text-white' : 'bg-white'}`}
                            onClick={() => setViewMode('list')}
                        >
                            Liste
                        </button>
                        <button 
                            className={`px-3 py-1 text-sm ${viewMode === 'calendar' ? 'bg-black text-white' : 'bg-white'}`}
                            onClick={() => setViewMode('calendar')}
                        >
                            Kalender
                        </button>
                    </div>
                    
                    <select 
                        className="border rounded px-2 py-1 text-sm"
                        value={filter.room}
                        onChange={(e) => setFilter({...filter, room: e.target.value})}
                    >
                        <option value="">Alle Räume</option>
                        {rooms.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                    </select>
                    <select 
                        className="border rounded px-2 py-1 text-sm"
                        value={filter.status}
                        onChange={(e) => setFilter({...filter, status: e.target.value})}
                    >
                        <option value="">Alle Status</option>
                        <option value="confirmed">Bestätigt</option>
                        <option value="cancelled">Storniert</option>
                    </select>
                    
                    {/* Export Button */}
                    <Button size="sm" variant="outline" onClick={exportToCSV}>
                        <Download size={14} className="mr-1" /> CSV Export
                    </Button>
                </div>
            </div>

            {/* Calendar View */}
            {viewMode === 'calendar' && (
                <Card>
                    <CardContent className="p-4">
                        <div className="flex justify-between items-center mb-4">
                            <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}>
                                <ChevronLeft size={20} />
                            </Button>
                            <h4 className="font-semibold text-lg">
                                {MONTHS_DE[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                            </h4>
                            <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}>
                                <ChevronRight size={20} />
                            </Button>
                        </div>
                        
                        <div className="grid grid-cols-7 gap-1">
                            {DAYS_DE.map(day => (
                                <div key={day} className="text-center font-semibold text-sm py-2 text-gray-500">{day}</div>
                            ))}
                            {getDaysInMonth(currentMonth).map((date, i) => {
                                const dayBookings = getBookingsForDate(date);
                                const isToday = date && date.toDateString() === new Date().toDateString();
                                
                                return (
                                    <div 
                                        key={i} 
                                        className={`min-h-[80px] border rounded p-1 ${
                                            !date ? 'bg-gray-50' : isToday ? 'bg-blue-50 border-blue-300' : 'bg-white'
                                        }`}
                                    >
                                        {date && (
                                            <>
                                                <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : ''}`}>
                                                    {date.getDate()}
                                                </div>
                                                <div className="space-y-0.5">
                                                    {dayBookings.slice(0, 3).map(b => (
                                                        <div 
                                                            key={b._id} 
                                                            className={`text-xs px-1 py-0.5 rounded truncate cursor-pointer ${
                                                                b.status === 'confirmed' 
                                                                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                                                    : 'bg-red-100 text-red-700'
                                                            }`}
                                                            title={`${b.room_name} - ${formatTime(b.start_time)}`}
                                                            onClick={() => setSelectedBooking(b)}
                                                        >
                                                            {formatTime(b.start_time)} {b.room_name?.substring(0, 3)}
                                                        </div>
                                                    ))}
                                                    {dayBookings.length > 3 && (
                                                        <div className="text-xs text-gray-500">+{dayBookings.length - 3} mehr</div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* List View */}
            {viewMode === 'list' && (
                <div className="space-y-2">
                    {filteredBookings.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">Keine Buchungen gefunden</p>
                    ) : (
                        filteredBookings.map(booking => (
                            <Card key={booking._id} className={booking.status === 'cancelled' ? 'opacity-50' : ''}>
                                <CardContent className="p-4">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold">{booking.room_name}</span>
                                                <span className={`text-xs px-2 py-0.5 rounded ${
                                                    booking.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                                }`}>
                                                    {booking.status === 'confirmed' ? 'Bestätigt' : 'Storniert'}
                                                </span>
                                            </div>
                                            <div className="text-sm text-gray-600 mt-1">
                                                <span className="flex items-center gap-1">
                                                    <Calendar size={14} /> {formatDate(booking.start_time)}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Clock size={14} /> {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                                                </span>
                                            </div>
                                            <div className="text-sm mt-1">
                                                <span className="font-medium">{booking.user_name || booking.guest_name || 'Gast'}</span>
                                                {(booking.user_email || booking.guest_email) && (
                                                    <span className="text-gray-500 ml-2">{booking.user_email || booking.guest_email}</span>
                                                )}
                                            </div>
                                            {booking.purpose && (
                                                <p className="text-sm text-gray-500 mt-1">Zweck: {booking.purpose}</p>
                                            )}
                                            {booking.special_requests && (
                                                <p className="text-sm text-orange-600 mt-1 flex items-center gap-1">
                                                    <FileText className="w-3 h-3" /> {booking.special_requests}
                                                </p>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm text-gray-500">Netto: {booking.total_price?.toFixed(2) || '0.00'}€</div>
                                            <div className="font-bold text-lg">{((booking.total_price || 0) * 1.19).toFixed(2)}€</div>
                                            <div className="text-xs text-gray-400">inkl. 19% MwSt.</div>
                                            <div className="text-sm text-gray-500 mt-1">{booking.participants_count} Pers.</div>
                                            <div className="flex gap-1 mt-2 justify-end">
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm"
                                                    onClick={() => setEmailPreview(booking)}
                                                    title="E-Mail Vorschau"
                                                >
                                                    <Eye size={14} />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm"
                                                    onClick={() => sendTestEmail(booking._id)}
                                                    title="Test-E-Mail senden"
                                                >
                                                    <Mail size={14} />
                                                </Button>
                                                {booking.status === 'confirmed' && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        className="text-red-500"
                                                        onClick={() => handleCancel(booking._id)}
                                                        title="Stornieren"
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>
            )}

            {/* Booking Detail Dialog - Extended with Admin Fields */}
            <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Buchungsdetails</DialogTitle>
                    </DialogHeader>
                    {selectedBooking && (
                        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="font-medium">Raum:</div>
                                <div>{selectedBooking.room_name}</div>
                                <div className="font-medium">Datum:</div>
                                <div>{formatDate(selectedBooking.start_time)}</div>
                                <div className="font-medium">Uhrzeit:</div>
                                <div>{formatTime(selectedBooking.start_time)} - {formatTime(selectedBooking.end_time)}</div>
                                <div className="font-medium">Name:</div>
                                <div>{selectedBooking.user_name || selectedBooking.guest_name || 'Gast'}</div>
                                <div className="font-medium">E-Mail:</div>
                                <div>{selectedBooking.user_email || selectedBooking.guest_email || '-'}</div>
                                <div className="font-medium">Teilnehmer:</div>
                                <div>{selectedBooking.participants_count}</div>
                                <div className="font-medium">Status:</div>
                                <div className="flex items-center gap-1">
                                    {selectedBooking.status === 'confirmed' 
                                        ? <><CheckCircle className="w-4 h-4 text-green-600" /> Bestätigt</>
                                        : <><XCircle className="w-4 h-4 text-red-600" /> Storniert</>
                                    }
                                </div>
                            </div>
                            {selectedBooking.purpose && (
                                <div>
                                    <div className="font-medium text-sm">Zweck:</div>
                                    <div className="text-sm text-gray-600">{selectedBooking.purpose}</div>
                                </div>
                            )}
                            {selectedBooking.special_requests && (
                                <div>
                                    <div className="font-medium text-sm">Besondere Wünsche:</div>
                                    <div className="text-sm text-orange-600">{selectedBooking.special_requests}</div>
                                </div>
                            )}
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="text-sm">Netto: {selectedBooking.total_price?.toFixed(2) || '0.00'} EUR</div>
                                <div className="text-sm">MwSt. (19%): {((selectedBooking.total_price || 0) * 0.19).toFixed(2)} EUR</div>
                                <div className="font-bold">Brutto: {((selectedBooking.total_price || 0) * 1.19).toFixed(2)} EUR</div>
                            </div>
                            
                            {/* Admin Fields */}
                            <div className="border-t pt-3 space-y-2">
                                <div className="text-sm font-semibold text-gray-700">Verwaltung</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs text-gray-500">Kostenstelle</label>
                                        <Input 
                                            data-testid="kostenstelle-input"
                                            value={selectedBooking.kostenstelle || ''}
                                            onChange={(e) => setSelectedBooking({...selectedBooking, kostenstelle: e.target.value})}
                                            placeholder="z.B. KST-001"
                                            className="h-8 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500">Projektnummer</label>
                                        <Input 
                                            data-testid="projektnummer-input"
                                            value={selectedBooking.projektnummer || ''}
                                            onChange={(e) => setSelectedBooking({...selectedBooking, projektnummer: e.target.value})}
                                            placeholder="z.B. PRJ-2026"
                                            className="h-8 text-sm"
                                        />
                                    </div>
                                </div>
                                {/* Positionen Editor */}
                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Positionen (G-Preis anpassbar)</div>
                                    <div className="bg-gray-50 rounded-lg overflow-hidden border">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="bg-gray-100 text-gray-600">
                                                    <th className="text-left px-2 py-1.5 font-medium">Beschreibung</th>
                                                    <th className="text-right px-2 py-1.5 font-medium w-16">Menge</th>
                                                    <th className="text-right px-2 py-1.5 font-medium w-20">E-Preis</th>
                                                    <th className="text-right px-2 py-1.5 font-medium w-24">G-Preis</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {/* Room Position */}
                                                <tr className="border-t">
                                                    <td className="px-2 py-1.5 text-gray-700">{selectedBooking.room_name || "Raum"}</td>
                                                    <td className="px-2 py-1.5 text-right text-gray-500">1</td>
                                                    <td className="px-2 py-1.5 text-right text-gray-500">{(selectedBooking.room_price || selectedBooking.total_price || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-1.5 text-right">
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            data-testid="price-override-room"
                                                            className="w-20 text-right text-xs border rounded px-1 py-0.5 bg-white"
                                                            value={(selectedBooking.price_overrides?.room ?? selectedBooking.room_price ?? selectedBooking.total_price ?? 0)}
                                                            onChange={(e) => {
                                                                const val = parseFloat(e.target.value) || 0;
                                                                setSelectedBooking({
                                                                    ...selectedBooking,
                                                                    price_overrides: {
                                                                        ...(selectedBooking.price_overrides || {}),
                                                                        room: val
                                                                    }
                                                                });
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                                {/* Addon Positions */}
                                                {(selectedBooking.addons || []).map((addon, i) => {
                                                    const addonKey = `addon_${i}`;
                                                    const ePreis = addon.price || 0;
                                                    const qty = addon.quantity || 1;
                                                    const calcTotal = ePreis * qty;
                                                    return (
                                                        <tr key={addonKey} className="border-t">
                                                            <td className="px-2 py-1.5 text-gray-700">{addon.name || addon.addon_name || `Zusatz ${i+1}`}</td>
                                                            <td className="px-2 py-1.5 text-right text-gray-500">{qty}</td>
                                                            <td className="px-2 py-1.5 text-right text-gray-500">{ePreis.toFixed(2)}</td>
                                                            <td className="px-2 py-1.5 text-right">
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    data-testid={`price-override-${addonKey}`}
                                                                    className="w-20 text-right text-xs border rounded px-1 py-0.5 bg-white"
                                                                    value={(selectedBooking.price_overrides?.[addonKey] ?? calcTotal)}
                                                                    onChange={(e) => {
                                                                        const val = parseFloat(e.target.value) || 0;
                                                                        setSelectedBooking({
                                                                            ...selectedBooking,
                                                                            price_overrides: {
                                                                                ...(selectedBooking.price_overrides || {}),
                                                                                [addonKey]: val
                                                                            }
                                                                        });
                                                                    }}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input 
                                        type="checkbox"
                                        data-testid="hide-prices-checkbox"
                                        checked={selectedBooking.hide_unit_prices || false}
                                        onChange={(e) => setSelectedBooking({...selectedBooking, hide_unit_prices: e.target.checked})}
                                    />
                                    <label className="text-sm text-gray-600">Einzelpreise auf Dokumenten ausblenden</label>
                                </div>
                                <Button 
                                    size="sm" 
                                    data-testid="save-admin-fields-btn"
                                    onClick={async () => {
                                        try {
                                            const token = localStorage.getItem('token');
                                            await axios.put(
                                                `${API_URL}/api/booking/bookings/${selectedBooking._id}/admin`,
                                                {
                                                    kostenstelle: selectedBooking.kostenstelle || null,
                                                    projektnummer: selectedBooking.projektnummer || null,
                                                    hide_unit_prices: selectedBooking.hide_unit_prices || false,
                                                    price_overrides: selectedBooking.price_overrides || null
                                                },
                                                { headers: { Authorization: `Bearer ${token}` } }
                                            );
                                            toast.success('Verwaltungsdaten gespeichert');
                                            fetchBookings();
                                        } catch (err) {
                                            toast.error('Fehler beim Speichern');
                                        }
                                    }}
                                    className="w-full"
                                >
                                    <Save size={14} className="mr-1" /> Speichern
                                </Button>
                            </div>
                            
                            {/* Document Generation */}
                            <div className="border-t pt-3 space-y-2">
                                <div className="text-sm font-semibold text-gray-700">Dokument erstellen</div>
                                <div className="flex gap-2">
                                    {['angebot', 'ab', 'rechnung'].map(docType => (
                                        <Button 
                                            key={docType}
                                            size="sm" 
                                            variant="outline"
                                            data-testid={`doc-${docType}-btn`}
                                            onClick={() => {
                                                window.open(
                                                    `${API_URL}/api/booking/bookings/${selectedBooking._id}/document/${docType}`,
                                                    '_blank'
                                                );
                                            }}
                                        >
                                            <FileText size={14} className="mr-1" />
                                            {docType === 'angebot' ? 'Angebot' : docType === 'ab' ? 'AB' : 'Rechnung'}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="flex gap-2 border-t pt-3">
                                <Button size="sm" variant="outline" onClick={() => setEmailPreview(selectedBooking)}>
                                    <Eye size={14} className="mr-1" /> E-Mail Vorschau
                                </Button>
                                {selectedBooking.status === 'confirmed' && (
                                    <Button size="sm" variant="destructive" onClick={() => { handleCancel(selectedBooking._id); setSelectedBooking(null); }}>
                                        <Trash2 size={14} className="mr-1" /> Stornieren
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Email Preview Dialog */}
            <Dialog open={!!emailPreview} onOpenChange={() => setEmailPreview(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>E-Mail Vorschau - Buchungsbestätigung</DialogTitle>
                    </DialogHeader>
                    {emailPreview && (
                        <div className="border rounded-lg overflow-hidden">
                            <div dangerouslySetInnerHTML={{ __html: generateEmailPreview(emailPreview) }} />
                        </div>
                    )}
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setEmailPreview(null)}>Schließen</Button>
                        <Button onClick={() => { sendTestEmail(emailPreview._id); setEmailPreview(null); }}>
                            <Mail size={14} className="mr-1" /> Test-E-Mail senden
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Addon Manager Component - Extended with tiered pricing, gallery, detailed description
const AddonManager = () => {
    const [addons, setAddons] = useState([]);
    const [editingAddon, setEditingAddon] = useState(null);
    const [isNew, setIsNew] = useState(false);

    useEffect(() => {
        fetchAddons();
    }, []);

    const fetchAddons = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/booking/addons`);
            setAddons(response.data);
        } catch (error) {
            toast.error("Fehler beim Laden der Add-ons");
        }
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('token');
            // Clean up data before saving
            const addonData = {
                ...editingAddon,
                price_tiers: editingAddon.price_type === 'tiered' ? editingAddon.price_tiers : null,
                gallery_images: editingAddon.gallery_images?.filter(img => img.trim()) || null
            };
            
            if (isNew) {
                await axios.post(`${API_URL}/api/booking/addons`, addonData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Add-on erstellt");
            } else {
                await axios.put(`${API_URL}/api/booking/addons/${editingAddon._id}`, addonData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Add-on aktualisiert");
            }
            setEditingAddon(null);
            setIsNew(false);
            fetchAddons();
        } catch (error) {
            toast.error("Fehler beim Speichern");
        }
    };

    const handleDelete = async (addonId) => {
        if (!window.confirm("Add-on wirklich löschen?")) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/booking/addons/${addonId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Add-on gelöscht");
            fetchAddons();
        } catch (error) {
            toast.error("Fehler beim Löschen");
        }
    };

    const addPriceTier = () => {
        const currentTiers = editingAddon.price_tiers || [];
        setEditingAddon({
            ...editingAddon,
            price_tiers: [...currentTiers, { min_quantity: 1, max_quantity: null, price_per_unit: 0 }]
        });
    };

    const updatePriceTier = (index, field, value) => {
        const newTiers = [...(editingAddon.price_tiers || [])];
        newTiers[index] = { ...newTiers[index], [field]: value === '' ? null : value };
        setEditingAddon({ ...editingAddon, price_tiers: newTiers });
    };

    const removePriceTier = (index) => {
        const newTiers = editingAddon.price_tiers.filter((_, i) => i !== index);
        setEditingAddon({ ...editingAddon, price_tiers: newTiers });
    };

    const newAddonTemplate = { 
        name: "", 
        description: "",
        detailed_description: "",
        price: 0, 
        price_type: "flat", 
        price_tiers: [],
        unit_name: "",
        min_quantity: 1,
        max_quantity: null,
        gallery_images: [],
        is_active: true 
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Zusatzleistungen (Add-ons)</h3>
                <Button onClick={() => { setIsNew(true); setEditingAddon(newAddonTemplate); }} size="sm">
                    <Plus size={16} className="mr-1" /> Neues Add-on
                </Button>
            </div>

            {editingAddon && (
                <Card className="border-2 border-blue-500">
                    <CardContent className="p-4 space-y-4">
                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium">Name *</label>
                                <Input 
                                    value={editingAddon.name}
                                    onChange={(e) => setEditingAddon({...editingAddon, name: e.target.value})}
                                    placeholder="z.B. Verpflegung / Catering"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Preistyp *</label>
                                <select 
                                    className="w-full border rounded-md p-2"
                                    value={editingAddon.price_type}
                                    onChange={(e) => setEditingAddon({...editingAddon, price_type: e.target.value})}
                                >
                                    <option value="flat">Festpreis</option>
                                    <option value="per_person">Pro Person</option>
                                    <option value="per_unit">Pro Einheit</option>
                                    <option value="tiered">Staffelpreise (Mengenrabatt)</option>
                                </select>
                            </div>
                        </div>

                        {/* Price based on type */}
                        {editingAddon.price_type !== 'tiered' && (
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="text-sm font-medium">Preis (€ netto)</label>
                                    <Input 
                                        type="number"
                                        step="0.01"
                                        value={editingAddon.price || 0}
                                        onChange={(e) => setEditingAddon({...editingAddon, price: parseFloat(e.target.value) || 0})}
                                    />
                                </div>
                                {(editingAddon.price_type === 'per_unit') && (
                                    <div>
                                        <label className="text-sm font-medium">Einheit</label>
                                        <Input 
                                            value={editingAddon.unit_name || ""}
                                            onChange={(e) => setEditingAddon({...editingAddon, unit_name: e.target.value})}
                                            placeholder="z.B. Portionen, Stück"
                                        />
                                    </div>
                                )}
                                <div>
                                    <label className="text-sm font-medium">Min. Teilnehmer</label>
                                    <Input 
                                        type="number"
                                        value={editingAddon.min_participants || 1}
                                        onChange={(e) => setEditingAddon({...editingAddon, min_participants: parseInt(e.target.value) || 1})}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Tiered Pricing */}
                        {editingAddon.price_type === 'tiered' && (
                            <div className="border rounded-lg p-4 bg-gray-50">
                                <div className="flex justify-between items-center mb-3">
                                    <label className="text-sm font-medium">Staffelpreise (Mengenrabatt)</label>
                                    <Button size="sm" variant="outline" onClick={addPriceTier}>
                                        <Plus size={14} className="mr-1" /> Staffel hinzufügen
                                    </Button>
                                </div>
                                <div className="grid grid-cols-3 gap-4 mb-2">
                                    <div>
                                        <label className="text-sm font-medium">Einheit</label>
                                        <Input 
                                            value={editingAddon.unit_name || ""}
                                            onChange={(e) => setEditingAddon({...editingAddon, unit_name: e.target.value})}
                                            placeholder="z.B. Portionen"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Min. Menge</label>
                                        <Input 
                                            type="number"
                                            value={editingAddon.min_quantity || 1}
                                            onChange={(e) => setEditingAddon({...editingAddon, min_quantity: parseInt(e.target.value) || 1})}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Max. Menge (leer = unbegrenzt)</label>
                                        <Input 
                                            type="number"
                                            value={editingAddon.max_quantity || ""}
                                            onChange={(e) => setEditingAddon({...editingAddon, max_quantity: e.target.value ? parseInt(e.target.value) : null})}
                                            placeholder="unbegrenzt"
                                        />
                                    </div>
                                </div>
                                
                                {(editingAddon.price_tiers || []).map((tier, index) => (
                                    <div key={index} className="grid grid-cols-4 gap-2 mb-2 items-end">
                                        <div>
                                            <label className="text-xs text-gray-500">Von (Menge)</label>
                                            <Input 
                                                type="number"
                                                value={tier.min_quantity || ""}
                                                onChange={(e) => updatePriceTier(index, 'min_quantity', parseInt(e.target.value) || 1)}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-500">Bis (Menge, leer=∞)</label>
                                            <Input 
                                                type="number"
                                                value={tier.max_quantity || ""}
                                                onChange={(e) => updatePriceTier(index, 'max_quantity', e.target.value ? parseInt(e.target.value) : null)}
                                                placeholder="∞"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-500">Preis/Einheit (€ netto)</label>
                                            <Input 
                                                type="number"
                                                step="0.01"
                                                value={tier.price_per_unit || ""}
                                                onChange={(e) => updatePriceTier(index, 'price_per_unit', parseFloat(e.target.value) || 0)}
                                            />
                                        </div>
                                        <Button size="sm" variant="ghost" onClick={() => removePriceTier(index)}>
                                            <Trash2 size={14} className="text-red-500" />
                                        </Button>
                                    </div>
                                ))}
                                
                                {(editingAddon.price_tiers?.length || 0) === 0 && (
                                    <p className="text-sm text-gray-500 text-center py-2">
                                        Fügen Sie Staffeln hinzu (z.B. 1-9 Stück: 15€, 10-19: 12€, 20+: 10€)
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Descriptions */}
                        <div>
                            <label className="text-sm font-medium">Kurzbeschreibung</label>
                            <Input 
                                value={editingAddon.description || ""}
                                onChange={(e) => setEditingAddon({...editingAddon, description: e.target.value})}
                                placeholder="Kurze Beschreibung für die Liste"
                            />
                        </div>
                        
                        <div>
                            <label className="text-sm font-medium">Detaillierte Beschreibung (HTML für Info-Popup)</label>
                            <textarea 
                                className="w-full border rounded-md p-2 min-h-[100px]"
                                value={editingAddon.detailed_description || ""}
                                onChange={(e) => setEditingAddon({...editingAddon, detailed_description: e.target.value})}
                                placeholder="<h3>Titel</h3><p>Beschreibung...</p><ul><li>Punkt 1</li></ul>"
                            />
                            <p className="text-xs text-gray-500 mt-1">HTML-Tags erlaubt: h3, p, ul, li, strong, em</p>
                        </div>

                        {/* Gallery Images */}
                        <div>
                            <label className="text-sm font-medium">Galerie-Bilder (URLs, eine pro Zeile)</label>
                            <textarea 
                                className="w-full border rounded-md p-2 min-h-[60px]"
                                value={(editingAddon.gallery_images || []).join('\n')}
                                onChange={(e) => setEditingAddon({...editingAddon, gallery_images: e.target.value.split('\n').filter(url => url.trim())})}
                                placeholder="/assets/addon/1.jpg&#10;/assets/addon/2.jpg"
                            />
                        </div>

                        {/* Active Toggle */}
                        <div className="flex items-center gap-2">
                            <input 
                                type="checkbox"
                                checked={editingAddon.is_active !== false}
                                onChange={(e) => setEditingAddon({...editingAddon, is_active: e.target.checked})}
                                className="w-4 h-4"
                            />
                            <label className="text-sm">Aktiv (wird Kunden angezeigt)</label>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-2 border-t">
                            <Button onClick={handleSave} size="sm">
                                <Save size={14} className="mr-1" /> Speichern
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => { setEditingAddon(null); setIsNew(false); }}>
                                <X size={14} className="mr-1" /> Abbrechen
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Addon List */}
            <div className="grid gap-3">
                {addons.map(addon => (
                    <Card key={addon._id} className={addon.is_active === false ? 'opacity-50' : ''}>
                        <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <h4 className="font-semibold flex items-center gap-2">
                                        <Package size={18} />
                                        {addon.name}
                                        {addon.is_active === false && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">Inaktiv</span>}
                                    </h4>
                                    <p className="text-sm text-gray-500">{addon.description}</p>
                                    <div className="text-sm text-gray-600 mt-1">
                                        {addon.price_type === 'tiered' ? (
                                            <span>
                                                Staffelpreise: {addon.price_tiers?.map((t, i) => (
                                                    <span key={i}>
                                                        {t.min_quantity}{t.max_quantity ? `-${t.max_quantity}` : '+'}: {t.price_per_unit}€
                                                        {i < addon.price_tiers.length - 1 ? ' | ' : ''}
                                                    </span>
                                                ))} / {addon.unit_name || 'Stück'}
                                            </span>
                                        ) : addon.price_type === 'per_person' ? (
                                            <span>{addon.price}€ / Person</span>
                                        ) : addon.price_type === 'per_unit' ? (
                                            <span>{addon.price}€ / {addon.unit_name || 'Stück'}</span>
                                        ) : (
                                            <span>{addon.price}€ Festpreis</span>
                                        )}
                                        <span className="text-xs text-gray-400 ml-2">(netto, zzgl. MwSt.)</span>
                                    </div>
                                    {addon.detailed_description && (
                                        <p className="text-xs text-blue-500 mt-1">✓ Info-Popup vorhanden</p>
                                    )}
                                    {addon.gallery_images?.length > 0 && (
                                        <p className="text-xs text-green-500">✓ {addon.gallery_images.length} Galerie-Bild(er)</p>
                                    )}
                                </div>
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="sm" onClick={() => { setEditingAddon(addon); setIsNew(false); }}>
                                        <Edit size={14} />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(addon._id)}>
                                        <Trash2 size={14} className="text-red-500" />
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
                {addons.length === 0 && (
                    <p className="text-gray-500 text-center py-4">Keine Add-ons vorhanden</p>
                )}
            </div>
        </div>
    );
};

// User Manager Component
const UserManager = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/booking/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(response.data);
        } catch (error) {
            toast.error("Fehler beim Laden der Benutzer");
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/api/booking/users/${userId}/role?role=${newRole}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Rolle auf "${newRole === 'internal' ? 'Intern' : 'Extern'}" geändert`);
            fetchUsers();
        } catch (error) {
            toast.error("Fehler beim Ändern der Rolle");
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm("Benutzer wirklich löschen? Alle Buchungen bleiben erhalten.")) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/booking/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Benutzer gelöscht");
            fetchUsers();
        } catch (error) {
            toast.error("Fehler beim Löschen");
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Registrierte Benutzer</h3>
                <span className="text-sm text-gray-500">{users.length} Benutzer</span>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <strong>Rollen:</strong><br/>
                • <span className="font-medium">Extern</span> = Zahlt für Raumbuchungen<br/>
                • <span className="font-medium">Intern</span> = Kostenlose Raumbuchungen (z.B. Mieter)
            </div>

            {loading ? (
                <p className="text-center py-8 text-gray-500">Lädt...</p>
            ) : users.length === 0 ? (
                <p className="text-center py-8 text-gray-500">Keine Benutzer registriert</p>
            ) : (
                <div className="space-y-2">
                    {users.map(user => (
                        <Card key={user.id}>
                            <CardContent className="p-4">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <UserCheck size={18} />
                                            <span className="font-semibold">{user.first_name} {user.last_name}</span>
                                            <span className={`text-xs px-2 py-0.5 rounded ${
                                                user.role === 'internal' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                            }`}>
                                                {user.role === 'internal' ? 'Intern' : 'Extern'}
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-500">{user.email}</div>
                                        {user.company && <div className="text-sm text-gray-400">{user.company}</div>}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <select 
                                            className="border rounded px-2 py-1 text-sm"
                                            value={user.role}
                                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                        >
                                            <option value="external">Extern</option>
                                            <option value="internal">Intern</option>
                                        </select>
                                        <Button variant="ghost" size="sm" onClick={() => handleDelete(user.id)}>
                                            <Trash2 size={14} className="text-red-500" />
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

// Cancellation Rules Manager Component
const CancellationRulesManager = () => {
    const [rules, setRules] = useState(null);
    const [editingRules, setEditingRules] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchRules();
    }, []);

    const fetchRules = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/booking/cancellation-rules`);
            setRules(response.data);
            setEditingRules(response.data);
        } catch (error) {
            // Set default rules if none exist
            const defaultRules = {
                tiers: [
                    { days_before: 14, fee_percent: 0 },
                    { days_before: 2, fee_percent: 50 }
                ],
                default_fee_percent: 100
            };
            setRules(defaultRules);
            setEditingRules(defaultRules);
        } finally {
            setLoading(false);
        }
    };

    const addTier = () => {
        const newTiers = [...(editingRules.tiers || [])];
        newTiers.push({ days_before: 7, fee_percent: 25 });
        setEditingRules({ ...editingRules, tiers: newTiers });
    };

    const removeTier = (index) => {
        const newTiers = editingRules.tiers.filter((_, i) => i !== index);
        setEditingRules({ ...editingRules, tiers: newTiers });
    };

    const updateTier = (index, field, value) => {
        const newTiers = [...editingRules.tiers];
        newTiers[index] = { ...newTiers[index], [field]: parseInt(value) || 0 };
        setEditingRules({ ...editingRules, tiers: newTiers });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            // Sort tiers by days_before descending before saving
            const sortedTiers = [...editingRules.tiers].sort((a, b) => b.days_before - a.days_before);
            const rulesData = {
                ...editingRules,
                tiers: sortedTiers
            };
            
            await axios.post(`${API_URL}/api/booking/admin/cancellation-rules`, rulesData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setRules(rulesData);
            toast.success('Stornierungsregeln gespeichert');
        } catch (error) {
            toast.error('Fehler beim Speichern der Regeln');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="text-center py-8 text-gray-500">Lädt...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-semibold">Stornierungsbedingungen</h3>
                    <p className="text-sm text-gray-500">Definieren Sie gestaffelte Stornierungsgebühren basierend auf der verbleibenden Zeit bis zur Buchung</p>
                </div>
            </div>

            <Card>
                <CardContent className="p-6 space-y-6">
                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                        <div className="font-semibold text-blue-800 mb-1">ℹ️ So funktioniert es:</div>
                        <ul className="text-blue-700 space-y-1 list-disc list-inside">
                            <li>Regeln werden von oben nach unten geprüft (längste Frist zuerst)</li>
                            <li><strong>Tage vor Termin</strong>: Mindestanzahl Tage vor dem Buchungstermin</li>
                            <li><strong>Gebühr %</strong>: Prozentsatz des Buchungsbetrags als Stornierungsgebühr</li>
                            <li>0% = Kostenlose Stornierung, 100% = Keine Rückerstattung</li>
                        </ul>
                    </div>

                    {/* Tiers */}
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <label className="font-medium">Stornierungsstaffeln</label>
                            <Button size="sm" variant="outline" onClick={addTier}>
                                <Plus size={14} className="mr-1" /> Staffel hinzufügen
                            </Button>
                        </div>
                        
                        {editingRules?.tiers?.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">Keine Staffeln definiert. Es gilt der Standard-Satz.</p>
                        ) : (
                            <div className="space-y-2">
                                {[...editingRules.tiers]
                                    .sort((a, b) => b.days_before - a.days_before)
                                    .map((tier, index) => (
                                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <div className="flex-1 grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-xs text-gray-500 block mb-1">Tage vor Termin (mind.)</label>
                                                    <Input 
                                                        type="number"
                                                        min={0}
                                                        value={tier.days_before}
                                                        onChange={(e) => updateTier(index, 'days_before', e.target.value)}
                                                        className="w-full"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-gray-500 block mb-1">Stornierungsgebühr (%)</label>
                                                    <Input 
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        value={tier.fee_percent}
                                                        onChange={(e) => updateTier(index, 'fee_percent', e.target.value)}
                                                        className="w-full"
                                                    />
                                                </div>
                                            </div>
                                            <div className="text-sm text-gray-600 min-w-[200px]">
                                                {tier.fee_percent === 0 
                                                    ? `≥${tier.days_before} Tage: Kostenlos`
                                                    : `≥${tier.days_before} Tage: ${tier.fee_percent}% Gebühr`
                                                }
                                            </div>
                                            <Button variant="ghost" size="sm" onClick={() => removeTier(index)}>
                                                <Trash2 size={14} className="text-red-500" />
                                            </Button>
                                        </div>
                                    ))
                                }
                            </div>
                        )}
                    </div>

                    {/* Default Fee */}
                    <div className="border-t pt-4">
                        <label className="font-medium block mb-2">Standard-Gebühr (unter Mindestfrist / No-Show)</label>
                        <div className="flex items-center gap-4">
                            <Input 
                                type="number"
                                min={0}
                                max={100}
                                value={editingRules?.default_fee_percent || 100}
                                onChange={(e) => setEditingRules({ ...editingRules, default_fee_percent: parseInt(e.target.value) || 0 })}
                                className="w-32"
                            />
                            <span className="text-gray-500">% des Buchungsbetrags</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                            Diese Gebühr gilt, wenn keine der obigen Staffeln zutrifft (z.B. bei kurzfristiger Stornierung oder Nichterscheinen)
                        </p>
                    </div>

                    {/* Preview */}
                    <div className="border-t pt-4">
                        <label className="font-medium block mb-2">Vorschau der Regeln</label>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <div className="font-semibold text-amber-800 mb-2">⚠️ Stornierungsbedingungen</div>
                            <ul className="text-amber-700 text-sm space-y-1 list-disc list-inside">
                                {[...editingRules.tiers]
                                    .sort((a, b) => b.days_before - a.days_before)
                                    .map((tier, i) => (
                                        <li key={i}>
                                            {tier.fee_percent === 0 
                                                ? `Mehr als ${tier.days_before} Tage vor Termin: Kostenlose Stornierung`
                                                : `${tier.days_before}+ Tage vor Termin: ${tier.fee_percent}% Stornierungsgebühr`
                                            }
                                        </li>
                                    ))
                                }
                                {editingRules.tiers.length > 0 && (
                                    <li>
                                        Weniger als {Math.min(...editingRules.tiers.map(t => t.days_before))} Tage: <strong>{editingRules.default_fee_percent}% Stornierungsgebühr</strong>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-4 border-t">
                        <Button onClick={handleSave} disabled={saving}>
                            <Save size={14} className="mr-1" />
                            {saving ? 'Speichert...' : 'Stornierungsregeln speichern'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Example calculation */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">📊 Beispielberechnung</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-gray-600 mb-4">
                        Bei einer Buchung über <strong>100€ netto (119€ brutto)</strong>:
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[...editingRules.tiers]
                            .sort((a, b) => b.days_before - a.days_before)
                            .map((tier, i) => {
                                const fee = 119 * (tier.fee_percent / 100);
                                const refund = 119 - fee;
                                return (
                                    <div key={i} className={`p-3 rounded-lg ${tier.fee_percent === 0 ? 'bg-green-50' : 'bg-amber-50'}`}>
                                        <div className="text-xs text-gray-500">≥{tier.days_before} Tage</div>
                                        <div className="font-bold">{tier.fee_percent}% Gebühr</div>
                                        <div className="text-sm">
                                            {tier.fee_percent === 0 
                                                ? <span className="text-green-600">Volle Rückerstattung</span>
                                                : <>Gebühr: {fee.toFixed(2)}€<br/>Rückerstattung: {refund.toFixed(2)}€</>
                                            }
                                        </div>
                                    </div>
                                );
                            })
                        }
                        <div className="p-3 rounded-lg bg-red-50">
                            <div className="text-xs text-gray-500">No-Show</div>
                            <div className="font-bold">{editingRules.default_fee_percent}% Gebühr</div>
                            <div className="text-sm text-red-600">
                                {editingRules.default_fee_percent === 100 
                                    ? 'Keine Rückerstattung' 
                                    : `Rückerstattung: ${(119 * (1 - editingRules.default_fee_percent / 100)).toFixed(2)}€`
                                }
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

// Main Booking Manager Component
export const BookingManager = () => {
    return (
        <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
                <Building2 size={28} />
                <div>
                    <h1 className="text-2xl font-bold">Raumbuchung</h1>
                    <p className="text-gray-500 text-sm">Verwaltung von Räumen, Buchungen und Benutzern</p>
                </div>
            </div>

            <Tabs defaultValue="bookings" className="w-full">
                <TabsList className="grid w-full grid-cols-5 mb-6">
                    <TabsTrigger value="bookings" className="flex items-center gap-1">
                        <Calendar size={16} /> Buchungen
                    </TabsTrigger>
                    <TabsTrigger value="rooms" className="flex items-center gap-1">
                        <DoorOpen size={16} /> Räume
                    </TabsTrigger>
                    <TabsTrigger value="addons" className="flex items-center gap-1">
                        <Package size={16} /> Add-ons
                    </TabsTrigger>
                    <TabsTrigger value="cancellation" className="flex items-center gap-1">
                        <X size={16} /> Stornierung
                    </TabsTrigger>
                    <TabsTrigger value="users" className="flex items-center gap-1">
                        <Users size={16} /> Benutzer
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="bookings">
                    <BookingOverview />
                </TabsContent>

                <TabsContent value="rooms">
                    <RoomManager />
                </TabsContent>

                <TabsContent value="addons">
                    <AddonManager />
                </TabsContent>

                <TabsContent value="cancellation">
                    <CancellationRulesManager />
                </TabsContent>

                <TabsContent value="users">
                    <UserManager />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default BookingManager;
